import {
  type CheckoutComponentProps,
  type CheckoutPaymentModelType,
  checkoutCommonSettings
} from '@elfsight/widget-checkout';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { noop } from 'es-toolkit';
import { meta } from '../../../meta';
import type { CheckoutBillingInterval, CustomFormField } from '../../../types';
import { useEmailSubmitFormSettings } from '../../components/email-submit-popup/form/use-email-submit-form-settings';
import { useEmailSubmitPopup } from '../../use-email-submit-popup';
import { useEmailSubmitPopupFields } from '../../use-email-submit-popup-fields';
import { useProductSourceData } from '../../utils';
import { useCalculationSummary } from './use-calculation-summary';
import { useCheckoutColors } from './use-checkout-colors';
import { useCheckoutDictionary } from './use-checkout-dictionary';
import { useInputsSummary } from './use-inputs-summary';

const {
  checkoutSuccessTitle: defaultSuccessTitle,
  checkoutSuccessText: defaultSuccessText
} = checkoutCommonSettings;

export function useCheckoutSettings(): CheckoutComponentProps | undefined {
  const { t } = useLingui();
  const {
    resultCTAHeading,
    resultCTACaption,
    recaptcha,
    integrationStripe,
    checkoutPaymentModel,
    checkoutBillingInterval,
    checkoutProductName,
    checkoutProductDescription,
    checkoutProductImage,
    checkoutSuccessText,
    checkoutSuccessTitle,
    widgetId,
    thousandsSeparator,
    decimalSeparator
  } = useSettings();

  const { isFormPopupOpen, closeFormPopup } = useEmailSubmitPopup();
  const { formFields, allFields } = useEmailSubmitPopupFields();
  const emailSubmitFormSettings = useEmailSubmitFormSettings(allFields);
  const productSourceData = useProductSourceData();
  const checkoutDictionary = useCheckoutDictionary();
  const calculationSummaryTable = useCalculationSummary();
  const inputsSummary = useInputsSummary();
  const checkoutColors = useCheckoutColors();

  const { cancellationPolicyText } = integrationStripe;

  if (!productSourceData) {
    return;
  }

  return {
    ...checkoutColors,
    isOpen: isFormPopupOpen,
    handleCloseRequest: closeFormPopup,
    dictionary: checkoutDictionary,
    withInternalForm: true,

    formPageSettings: {
      formLayoutProps: {
        headingText: resultCTAHeading,
        captionText: resultCTACaption,
        footerText: cancellationPolicyText,
        getDisplayedFields
      },

      formSettings: { ...emailSubmitFormSettings, recaptcha }
    },

    paymentPageSettings: {
      productData: {
        name: checkoutProductName || productSourceData.sourceName,
        amount: productSourceData.amount,
        currency: productSourceData.currency,
        quantity: 1,
        ...getCheckoutBillingData(checkoutPaymentModel, checkoutBillingInterval)
      },

      paymentMetadata: {
        widgetId: widgetId ?? 'calculator-widget',
        paymentContext: meta.alias
      },

      emailFieldId: formFields.find(({ type }) => type === 'email')?.id,

      onPaymentSuccess: noop,
      footerText: cancellationPolicyText
    },

    successMessageSettings: {
      heading: checkoutSuccessTitle || defaultSuccessTitle,
      description: checkoutSuccessText || defaultSuccessText,
      buttonText: t`Ok`
    },

    summarySettings: {
      productCard: {
        label: checkoutProductName,
        description: checkoutProductDescription,
        imageSrc: checkoutProductImage?.url
      },

      tables: [inputsSummary, calculationSummaryTable],

      totalLabel: t`Total`,

      totalPrice: {
        pricingModel: checkoutPaymentModel,
        price: productSourceData.amount,
        currency: productSourceData.currency,
        period: checkoutBillingInterval,
        thousandsSeparator,
        decimalSeparator,

        // типизация Price оставляет желать лучшего
        minPrice: 0,
        maxPrice: 0,
        customPrice: '',
        unit: 'day'
      }
    }
  };
}

function getDisplayedFields<F extends CustomFormField>(fields: F[]) {
  return fields.filter(
    ({ isCalculatorField, visible }) =>
      !isCalculatorField && (visible || visible === undefined)
  );
}

const billingIntervalStripeMap = {
  week: {
    recurringInterval: 'week',
    recurringIntervalCount: 1
  },
  month: {
    recurringInterval: 'month',
    recurringIntervalCount: 1
  },
  quarter: {
    recurringInterval: 'month',
    recurringIntervalCount: 3
  },
  '6 months': {
    recurringInterval: 'month',
    recurringIntervalCount: 6
  },
  year: {
    recurringInterval: 'year',
    recurringIntervalCount: 1
  }
} as const;

function getCheckoutBillingData(
  checkoutPaymentModel: CheckoutPaymentModelType,
  checkoutBillingInterval: CheckoutBillingInterval
) {
  if (checkoutPaymentModel === 'fixed') {
    return {};
  }

  return billingIntervalStripeMap[checkoutBillingInterval];
}
